import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useThemeMode, ThemePreference } from '../theme/ThemeModeContext';
import {
  useDateTimeFormat,
  TimeFormat,
  DateFormat,
} from '../preferences/DateTimeFormatContext';
import { PageHeader } from '../components/PageHeader';
import { LanguageToggle } from '../components/LanguageToggle';

export function Profile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { preference, setPreference } = useThemeMode();
  const { timeFormat, dateFormat, setTimeFormat, setDateFormat } = useDateTimeFormat();

  if (!user) return null;

  const handleThemeChange = (_: unknown, value: ThemePreference | null) => {
    if (value) setPreference(value);
  };

  const handleTimeFormatChange = (_: unknown, value: TimeFormat | null) => {
    if (value) setTimeFormat(value);
  };

  const handleDateFormatChange = (_: unknown, value: DateFormat | null) => {
    if (value) setDateFormat(value);
  };

  return (
    <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
    <Box sx={{ maxWidth: { xs: '100%', sm: 720 }, mx: 'auto', width: '100%' }}>
      <PageHeader />
    </Box>
    <Stack spacing={3} sx={{ maxWidth: { xs: '100%', sm: 720 }, mx: 'auto', width: '100%' }}>
      <Typography variant="h4">{t('profile.title')}</Typography>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar src={user.avatar_url ?? undefined} sx={{ width: 96, height: 96 }}>
              {user.name[0]}
            </Avatar>
            <Stack spacing={0.5}>
              <Typography variant="h5">{user.name}</Typography>
              {user.username && (
                <Typography variant="body2" color="text.secondary">
                  @{user.username}
                </Typography>
              )}
              <Typography variant="body2">{user.email}</Typography>
              {(user.city || user.country) && (
                <Typography variant="body2">
                  {[user.city, user.country].filter(Boolean).join(', ')}
                </Typography>
              )}
            </Stack>
          </Stack>
          {user.bio && (
            <Typography variant="body1" sx={{ mt: 3 }}>
              {user.bio}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile.language')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <LanguageToggle />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile.theme')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ToggleButtonGroup value={preference} exclusive onChange={handleThemeChange}>
            <ToggleButton value="system">{t('themes.system')}</ToggleButton>
            <ToggleButton value="light">{t('themes.light')}</ToggleButton>
            <ToggleButton value="dark">{t('themes.dark')}</ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile.dateFormat')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ToggleButtonGroup value={dateFormat} exclusive onChange={handleDateFormatChange}>
            <ToggleButton value="eu">{t('dateFormats.eu')}</ToggleButton>
            <ToggleButton value="us">{t('dateFormats.us')}</ToggleButton>
            <ToggleButton value="iso">{t('dateFormats.iso')}</ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile.timeFormat')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ToggleButtonGroup value={timeFormat} exclusive onChange={handleTimeFormatChange}>
            <ToggleButton value="24h">{t('timeFormats.24h')}</ToggleButton>
            <ToggleButton value="12h">{t('timeFormats.12h')}</ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>
    </Stack>
    </Box>
  );
}
